/**
 * Os fatos que o paywall de login afirma sobre cada plano.
 *
 * POR QUE EXISTE: estas quatro funções eram privadas de
 * `PaymentRequiredPlanGrid`. Quando o comparativo lado a lado (#244) passou a
 * mostrar os MESMOS planos numa segunda leitura, copiá-las criaria duas
 * verdades sobre "quanto tempo vale o anual" — o erro que
 * `monthly-equivalent.ts` já pagou uma vez com o rateio cravado em string.
 * Cartão e comparativo leem daqui; nenhum dos dois inventa fato.
 *
 * @example `formatPlanTerm({ periodDurationDays: 365, ... })` → `"12 meses"`
 */
import { monthlyEquivalentPrice } from "@/features/subscriptions/monthly-equivalent";
import type { PublicPlanView } from "../types";

/** Ordem canônica do paywall — e, por tabela, quais ids ele mostra. */
export const LOGIN_PLAN_ORDER = ["mensal", "semestral", "anual"] as const;

const LOGIN_PLAN_ORDER_INDEX = new Map<string, number>(
  LOGIN_PLAN_ORDER.map((planId, index) => [planId, index]),
);

/** Sem duplicatas, só os ids canônicos, na ordem da tabela de preços. */
export function normalizeVisiblePlans(
  plans: readonly PublicPlanView[],
): readonly PublicPlanView[] {
  return [...plans]
    .filter(
      (plan, index, allPlans) =>
        allPlans.findIndex((candidate) => candidate.id === plan.id) === index,
    )
    .filter((plan) => LOGIN_PLAN_ORDER_INDEX.has(plan.id))
    .sort((left, right) => planOrder(left) - planOrder(right));
}

function planOrder(plan: PublicPlanView): number {
  return LOGIN_PLAN_ORDER_INDEX.get(plan.id) ?? Number.MAX_SAFE_INTEGER;
}

export function formatPlanTerm(plan: PublicPlanView): string {
  const duration = plan.periodDurationDays;
  if (duration === null) return "liberação imediata";
  if (duration >= 365) return "12 meses";
  if (duration >= 180) return "6 meses";
  if (duration >= 30) return "30 dias";
  return "liberação imediata";
}

export function formatPlanSupport(plan: PublicPlanView): string {
  if (plan.id === "anual") {
    return "mais estabilidade por ciclo, menos fricção de renovação";
  }
  if (plan.id === "semestral") {
    return "equilíbrio entre entrada menor e permanência mais longa";
  }
  return "entrada rápida para voltar a operar hoje";
}

export function formatPlanHeadline(plan: PublicPlanView): string {
  if (plan.id === "anual") return "Plano anual com tokens ilimitados e IA Pro";
  if (plan.id === "semestral") {
    return "Plano semestral com 400 tokens de análise";
  }
  if (plan.id === "mensal") return "Plano mensal com 100 tokens de análise";
  return plan.description ?? formatPlanSupport(plan);
}

export function formatMonthlyEquivalent(plan: PublicPlanView): string | null {
  return monthlyEquivalentPrice(plan.amountCents, plan.periodDurationDays);
}
